/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
'use client';

import 'client-only';
import { clsx } from '@accelint/design-foundation/lib/utils';
import { Provider } from 'react-aria-components/slots';
import { DividerContext } from '../divider/context';
import styles from './styles.module.css';
import type { SidenavContentProps } from './types';

/**
 * SidenavContent - Container for sidenav content
 *
 * Provides a container for sidenav content with proper styling
 *
 * @example
 * ```tsx
 * <Sidenav>
 *   <SidenavContent>
 *     <SidenavItem textValue="Dashboard">
 *       <Icon><Home /></Icon>
 *       <Text>Dashboard</Text>
 *     </SidenavItem>
 *     <SidenavMenu icon={<Settings />} title="Settings">
 *       <SidenavMenuItem>Profile</SidenavMenuItem>
 *     </SidenavMenu>
 *   </SidenavContent>
 * </Sidenav>
 * ```
 *
 * @param props - {@link SidenavContentProps}
 * @param props.className - CSS class for the content container.
 * @param props.children - Content items (items, menus, dividers).
 * @returns The rendered SidenavContent component.
 */
export function SidenavContent({
  className,
  children,
  ...rest
}: SidenavContentProps) {
  return (
    <Provider values={[[DividerContext, { className: styles.divider }]]}>
      <div {...rest} className={clsx(styles.content, className)}>
        {children}
      </div>
    </Provider>
  );
}
